'use client';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React from 'react';

import NavigationItem from '@/components/navigation/NavigationItem';
import PartnerLinksDropdown from '@/components/navigation/PartnerLinksDropdown';
import SecondaryLogo, { getSecondaryLogoConfig } from '@/components/navigation/SecondaryLogo';
import ShopLinksDropdown from '@/components/navigation/ShopLinksDropdown';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { CinemaChainDto } from '@cineamo/api/models/cinema-chain/CinemaChainDto.types';
import { getShopLinks, resolveShopLinkHref } from '@cineamo/api/models/website-configuration/shop-link-helper';
import { WebsiteNavigation } from '@cineamo/api/models/website-configuration/WebsiteNavigaton.enum';

import {
    ID_CAPITOL_HEIDENHEIM,
    ID_KINO_CENTER_HEIDENHEIM,
    ID_KINO_REGINA_ELLWANGEN,
    ID_KINOPARK_AALEN
} from '@cineamo/api/constants/cinema-ids';

import { isNavigationVisible } from '@/helpers/navigation-helper';

import { IconBar24 } from '@cineamo/ui/src/icons/ic-bar.tsx';
import { IconBarSolid24 } from '@cineamo/ui/src/icons/ic-barSolid.tsx';
import { IconDeals24 } from '@cineamo/ui/src/icons/ic-deals';
import { IconDealsSolid24 } from '@cineamo/ui/src/icons/ic-dealsSolid';
import { IconHome24 } from '@cineamo/ui/src/icons/ic-home';
import { IconHomeSolid24 } from '@cineamo/ui/src/icons/ic-homeSolid';
import { IconMegaphone24 } from '@cineamo/ui/src/icons/ic-megaphone';
import { IconMegaphoneSolid24 } from '@cineamo/ui/src/icons/ic-megaphoneSolid';
import { IconPopcornBag24 } from '@cineamo/ui/src/icons/ic-popcornBag';
import { IconProfile24 } from '@cineamo/ui/src/icons/ic-profile';
import { IconProgram24 } from '@cineamo/ui/src/icons/ic-program';
import { IconProgramSolid24 } from '@cineamo/ui/src/icons/ic-programSolid';
import { IconStar24 } from '@cineamo/ui/src/icons/ic-star';
import { IconStarSolid24 } from '@cineamo/ui/src/icons/ic-starSolid';
import { IconVoucher24 } from '@cineamo/ui/src/icons/ic-voucher';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

import CinemaLogo from './CinemaLogo';
import NavigationItemInfo from './NavigationItemInfo';

type NavigationProps = {
    hasShowingTags: boolean;
    hasAnnouncements: boolean;
    cinemaChain: CinemaChainDto | null;
    cinemasInChain: CinemaDto[];
};

export const isInfoHiddenForCinema = (cinema: CinemaDto) =>
    [
        // Hide Info Item for stable Kinos
        ID_KINOPARK_AALEN,
        ID_KINO_REGINA_ELLWANGEN,
        ID_KINO_CENTER_HEIDENHEIM,
        ID_CAPITOL_HEIDENHEIM
    ].includes(cinema.id);

export default function Navigation(props: NavigationProps) {
    const { hasShowingTags, hasAnnouncements, cinemaChain, cinemasInChain } = props;
    const domainConfiguration = useDomainConfiguration();
    const partnerLinksDropdownConfiguration = domainConfiguration.websiteConfiguration.partnerLinksDropdown;

    const t = useTranslations();

    const cinema = domainConfiguration.cinema;

    const websiteConfiguration = cinema._embedded?.cinemaConfiguration?.websiteConfiguration;
    const navigationItems = websiteConfiguration?.navigation;
    const isNavVisible = (section: WebsiteNavigation) => isNavigationVisible(navigationItems, section);

    const shopLinks = getShopLinks(websiteConfiguration);
    const resolvedShopItems = shopLinks
        .map((link) => {
            const href = resolveShopLinkHref(link);
            if (!href) return null;
            const defaultLabelMap = {
                voucher: t('navigation.vouchers'),
                'snack-shop': t('navigation.snack-shop'),
                login: t('navigation.login')
            };
            return {
                href,
                title: link.customName || defaultLabelMap[link.type],
                type: link.type
            };
        })
        .filter((item): item is { href: string; title: string; type: (typeof shopLinks)[0]['type'] } => item !== null);

    const showShops = resolvedShopItems.length > 0 && isNavVisible(WebsiteNavigation.SHOPS);
    const showPartnerLinksDropdown = partnerLinksDropdownConfiguration?.isVisible === true;
    const secondaryLogoConfig = getSecondaryLogoConfig(websiteConfiguration?.secondaryLogo);

    return (
        <nav className={classnames('relative flex items-end justify-between flex-wrap w-full h-74 lg:min-h-120')}>
            {/* Border Bottom */}
            <div className="absolute left-0 bottom-0 w-full border-b-2 border-[var(--on-background-color)] opacity-20" />

            <div className="w-full flex flex-col md:flex-col xl:flex-row items-center container mx-auto justify-center space-x-0 xl:space-x-[72px]">
                <div
                    className={classnames(
                        'w-full flex flex-row items-center lg:w-auto lg:justify-normal lg:flex-row-reverse',
                        showPartnerLinksDropdown ? 'justify-between' : 'justify-center'
                    )}>
                    {showPartnerLinksDropdown && (
                        <PartnerLinksDropdown
                            cinemaChain={cinemaChain}
                            cinemasInChain={cinemasInChain}
                        />
                    )}
                    <div
                        className={classnames(
                            'flex flex-row items-center',
                            secondaryLogoConfig && 'justify-between',
                            secondaryLogoConfig && showPartnerLinksDropdown && 'flex-1',
                            secondaryLogoConfig && !showPartnerLinksDropdown && 'w-full',
                            !secondaryLogoConfig && !showPartnerLinksDropdown && 'w-full justify-center',
                            'lg:w-auto lg:justify-normal lg:space-x-[64px]'
                        )}>
                        <CinemaLogo
                            className={classnames({
                                'justify-start lg:justify-center': secondaryLogoConfig
                            })}
                        />

                        {secondaryLogoConfig && (
                            <SecondaryLogo
                                {...secondaryLogoConfig}
                                className="flex lg:hidden justify-end"
                            />
                        )}
                    </div>
                    {showPartnerLinksDropdown && <div className="w-[64px] h-[40px] lg:hidden"></div>}
                </div>
                <div className="hidden lg:flex flex-row w-full justify-center">
                    {isNavVisible(WebsiteNavigation.HOME) && (
                        <NavigationItem
                            href={'/'}
                            icon={<IconHome24 />}
                            activeIcon={<IconHomeSolid24 />}
                            title={t('navigation.home')}
                            item="home"
                        />
                    )}
                    {isNavVisible(WebsiteNavigation.PROGRAM) && (
                        <NavigationItem
                            href={'/program'}
                            icon={<IconProgram24 />}
                            activeIcon={<IconProgramSolid24 />}
                            title={t('navigation.program')}
                            item="program"
                        />
                    )}
                    {hasShowingTags && isNavVisible(WebsiteNavigation.SPECIALS) && (
                        <NavigationItem
                            href={'/specials'}
                            icon={<IconStar24 />}
                            activeIcon={<IconStarSolid24 />}
                            title={t('navigation.specials')}
                            item="specials"
                        />
                    )}
                    {isNavVisible(WebsiteNavigation.DEALS) && (
                        <NavigationItem
                            href={'/deals'}
                            icon={<IconDeals24 />}
                            activeIcon={<IconDealsSolid24 />}
                            title={t('navigation.deals')}
                            item="deals"
                        />
                    )}
                    {showShops && resolvedShopItems.length === 1 && (
                        <NavigationItem
                            trackClickEvent={
                                resolvedShopItems[0].type === 'voucher' ? 'Voucher Link Clicked' : undefined
                            }
                            href={resolvedShopItems[0].href}
                            icon={
                                resolvedShopItems[0].type === 'snack-shop' ? (
                                    <IconPopcornBag24 />
                                ) : resolvedShopItems[0].type === 'login' ? (
                                    <IconProfile24 />
                                ) : (
                                    <IconVoucher24 />
                                )
                            }
                            title={resolvedShopItems[0].title}
                            item={resolvedShopItems[0].type}
                        />
                    )}
                    {showShops && resolvedShopItems.length > 1 && (
                        <ShopLinksDropdown
                            items={resolvedShopItems}
                            triggerTitle={t('navigation.shop')}
                        />
                    )}
                    {hasAnnouncements && isNavVisible(WebsiteNavigation.ANNOUNCEMENTS) && (
                        <NavigationItem
                            href={'/announcements'}
                            icon={<IconMegaphone24 />}
                            activeIcon={<IconMegaphoneSolid24 />}
                            title={t('navigation.announcements')}
                            item="announcements"
                        />
                    )}
                    {isNavVisible(WebsiteNavigation.BAR) && (
                        <NavigationItem
                            href={'/bar'}
                            icon={<IconBar24 />}
                            activeIcon={<IconBarSolid24 />}
                            title={t('navigation.bar')}
                            item="bar"
                        />
                    )}
                    {!isInfoHiddenForCinema(cinema) && isNavVisible(WebsiteNavigation.INFO) && (
                        <NavigationItemInfo cinemaId={cinema.id} />
                    )}

                    {/*<NavigationItem*/}
                    {/*    className="w-160 xl:w-180 2xl:w-250 h-48"*/}
                    {/*    href={'https://www.cineamo.com'}*/}
                    {/*    icon={<IconCineamoOutline24 />}*/}
                    {/*    title={t('navigation.to-cineamo')}*/}
                    {/*    classNameTitle="hidden xl:block"*/}
                    {/*/>*/}
                </div>

                {secondaryLogoConfig && (
                    <SecondaryLogo
                        {...secondaryLogoConfig}
                        className={classnames('hidden lg:flex')}
                    />
                )}
            </div>
        </nav>
    );
}
